import { roundSahParticipantContribution } from './sah-contribution-rounding';
/**
 * Support at Home (SAH) contribution calculator — pure functions for computing
 * a participant's per-service contribution under the post-1-November-2025 SAH
 * contribution framework.
 *
 * Rates are imported from `regulatory-rates.ts` which was verified 2026-04-07
 * against the Department of Health Schedule of Fees and Charges for
 * Residential Care (20 March 2026) and related SAH publications.
 *
 * CRITICAL: Sliding-scale tiers (PART_PENSIONER, CSHC_HOLDER) MUST have an
 * individualAssessedRate passed in. The function THROWS if called without
 * one, because applying midpoint defaults would overcharge clients and is a
 * pecuniary penalty offence under Aged Care Act 2024 Ch 4 ss 400-430.
 *
 * Citation:
 *  - Aged Care Act 2024 Chapter 4 (participant contributions)
 *  - Aged Care Rules 2025 Chapter 4 (SAH contribution framework)
 *  - DOH Schedule of Fees and Charges 20 March 2026
 *    (docs/regulatory-sources/doh-schedule-of-fees-residential-20-mar-2026.pdf)
 *  - Services Australia SAH contribution publications
 */
import {
  SAH_FIXED_CONTRIBUTION_RATES,
  SAH_GRANDFATHERED_FIXED_RATES,
  SAH_SLIDING_SCALE_RANGES,
  SAH_GRANDFATHERED_SLIDING_RANGES,
  sahContributionCapsFor,
  type SahMeansTier,
  type SahServiceCategory,
} from './regulatory-rates';

export type { SahMeansTier, SahServiceCategory };

/**
 * Sliding-scale tier identifiers — PART_PENSIONER and CSHC_HOLDER both use
 * Services Australia individually-assessed rates and MUST not be given a
 * default by the platform.
 */
const SLIDING_SCALE_TIERS: readonly SahMeansTier[] = ['PART_PENSIONER', 'CSHC_HOLDER'];

export interface SahContributionInput {
  /** Service category: CLINICAL (0%), INDEPENDENCE (5-50%), EVERYDAY_LIVING (17.5-80%) */
  serviceCategory: SahServiceCategory;
  /** Gross service cost in AUD (before contribution split) */
  serviceCost: number;
  /** Client's means-test tier — determines which rate table applies */
  meansTier: SahMeansTier;
  /**
   * Individually-assessed rate from the Services Australia assessment letter,
   * as a decimal (e.g., 0.30 = 30%). REQUIRED for PART_PENSIONER and
   * CSHC_HOLDER tiers. The function throws if called with a sliding-scale
   * tier and no assessed rate.
   */
  individualAssessedRate?: number;
  /** Running total of the client's lifetime contributions to date (for cap enforcement) */
  lifetimeContributionsToDate?: number;
  /**
   * If true, the client is a grandfathered HCP participant under the "no
   * worse off" transitional provisions and the grandfathered lifetime cap
   * applies instead of the new-entrant cap.
   */
  isGrandfathered?: boolean;
  /**
   * Day the service is delivered or the estimate is for (YYYY-MM-DD). The
   * lifetime cap indexed for that day applies. Omit for the current cap.
   */
  asOfDate?: string | null;
}

export interface SahContributionBreakdown {
  serviceCost: number;
  /** The contribution rate actually applied (0-1). For clinical: always 0. */
  contributionRate: number;
  /** Dollar amount the client pays */
  clientContribution: number;
  /** Dollar amount the government pays (= serviceCost - clientContribution) */
  governmentSubsidy: number;
  /** Whether the lifetime cap has been reached or exceeded */
  capStatus: 'within' | 'at_cap' | 'exceeded';
  /** Lifetime cap that applies to this client ($137,917.01 new entrant / $86,185.23 grandfathered) */
  lifetimeCap: number;
  /** Remaining headroom under the lifetime cap after this contribution */
  remainingLifetimeCap: number;
  /** Warnings (e.g., assessed rate outside valid sliding-scale range) */
  warnings: string[];
}

/**
 * Compute the client's contribution for a single SAH service delivery.
 *
 * @throws {Error} If the tier is PART_PENSIONER or CSHC_HOLDER and no
 *   individualAssessedRate is provided. Midpoint defaults are NEVER applied
 *   to sliding-scale tiers — doing so would overcharge clients and breach
 *   the Aged Care Act 2024 Chapter 4 contribution provisions.
 */
export function calculateSahContribution(input: SahContributionInput): SahContributionBreakdown {
  const warnings: string[] = [];
  const {
    serviceCategory,
    serviceCost,
    meansTier,
    individualAssessedRate,
    lifetimeContributionsToDate = 0,
    isGrandfathered = false,
  } = input;

  // ── Determine the applicable lifetime cap ──
  const caps = sahContributionCapsFor(input.asOfDate);
  const lifetimeCap = isGrandfathered ? caps.lifetimeCapGrandfathered : caps.lifetimeCapNewEntrant;

  // ── Lifetime cap check (enforced BEFORE rate calculation) ──
  if (lifetimeContributionsToDate >= lifetimeCap) {
    return {
      serviceCost,
      contributionRate: 0,
      clientContribution: 0,
      governmentSubsidy: serviceCost,
      capStatus: 'exceeded',
      lifetimeCap,
      remainingLifetimeCap: 0,
      warnings: [
        `Client has reached the SAH lifetime contribution cap of $${lifetimeCap.toLocaleString()}. All further services are 100% government-funded.`,
      ],
    };
  }

  // ── Clinical services are always 0% client contribution ──
  if (serviceCategory === 'CLINICAL') {
    return {
      serviceCost,
      contributionRate: 0,
      clientContribution: 0,
      governmentSubsidy: round2(serviceCost),
      capStatus: 'within',
      lifetimeCap,
      remainingLifetimeCap: lifetimeCap - lifetimeContributionsToDate,
      warnings,
    };
  }

  // ── Resolve the contribution rate based on tier and grandfathered status ──
  //
  // Grandfathered ("no worse off") clients — those who were on or approved for
  // a Home Care Package on 12 September 2024 — pay LOWER contribution rates than
  // standard SAH entrants. The rate tables are completely separate; failing to
  // apply the grandfathered table to a no-worse-off client overcharges them and
  // is a billing offence under Aged Care Act 2024 Chapter 4 ss 400-430.
  //
  // Source: docs/regulatory-sources/doh-sah-participant-contributions-2025-12.pdf
  //   page 2 = standard rates table; page 3 = "no worse off" grandfathered table.
  const fixedRates = isGrandfathered ? SAH_GRANDFATHERED_FIXED_RATES : SAH_FIXED_CONTRIBUTION_RATES;
  const slidingRanges = isGrandfathered
    ? SAH_GRANDFATHERED_SLIDING_RANGES
    : SAH_SLIDING_SCALE_RANGES;

  let rate: number;

  if (SLIDING_SCALE_TIERS.includes(meansTier)) {
    if (individualAssessedRate === undefined || individualAssessedRate === null) {
      throw new Error(
        `SAH contribution calculation rejected: tier "${meansTier}" is sliding-scale and REQUIRES an individualAssessedRate from the Services Australia assessment letter. Applying a default midpoint would overcharge the client and breach Aged Care Act 2024 Ch 4 ss 400-430. Upload the assessment letter and capture the assessed rate per service category before computing contributions.`,
      );
    }
    // Validate the assessed rate is within the legislated range for this client's
    // grandfathered/standard status — the ranges differ (grandfathered max is 25%,
    // standard max is 50% Independence / 80% Everyday Living).
    const range = slidingRanges[serviceCategory];
    if (individualAssessedRate < range.min || individualAssessedRate > range.max) {
      warnings.push(
        // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
        `Assessed rate ${(individualAssessedRate * 100).toFixed(1)}% is outside the SAH ${isGrandfathered ? 'no-worse-off ' : ''}sliding-scale range for ${serviceCategory} (${(range.min * 100).toFixed(1)}%-${(range.max * 100).toFixed(1)}%). Re-verify against the Services Australia assessment letter.`,
      );
    }
    rate = Math.min(range.max, Math.max(range.min, individualAssessedRate));
  } else if (meansTier === 'FULL_PENSIONER') {
    rate = fixedRates.FULL_PENSIONER[serviceCategory];
  } else if (meansTier === 'SELF_FUNDED_NO_CSHC') {
    rate = fixedRates.SELF_FUNDED_NO_CSHC[serviceCategory];
  } else {
    // Exhaustiveness guard
    throw new Error(`Unknown SAH means tier: ${String(meansTier)}`);
  }

  // ── Apply the rate ──
  let clientContribution = roundSahParticipantContribution(serviceCost, rate, 'fraction');

  // ── Clamp to remaining lifetime cap headroom ──
  const remainingHeadroom = lifetimeCap - lifetimeContributionsToDate;
  let capStatus: 'within' | 'at_cap' | 'exceeded' = 'within';
  if (clientContribution >= remainingHeadroom) {
    clientContribution = round2(remainingHeadroom);
    capStatus = 'at_cap';
    warnings.push(
      `Client will reach the SAH lifetime cap of $${lifetimeCap.toLocaleString()} with this contribution. Further services after this one will be 100% government-funded.`,
    );
  }

  // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
  const governmentSubsidy = round2(serviceCost - clientContribution);
  const remainingLifetimeCap = round2(
    // eslint-disable-next-line statura/no-floating-point-money -- canonical SAH/SCHADS/residential regulatory calc, validated against FWC pay guide / Services Australia
    lifetimeCap - (lifetimeContributionsToDate + clientContribution),
  );

  return {
    serviceCost,
    contributionRate: rate,
    clientContribution,
    governmentSubsidy,
    capStatus,
    lifetimeCap,
    remainingLifetimeCap,
    warnings,
  };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Helper: map the prod DB enum values (tier_1..tier_4) to canonical tier names.
 * The DB uses generic tier_1..tier_4 because the numbering is stable even if
 * Services Australia renames tiers. Convention (must match client intake flow):
 *   tier_1 → FULL_PENSIONER
 *   tier_2 → PART_PENSIONER (sliding-scale)
 *   tier_3 → CSHC_HOLDER (sliding-scale)
 *   tier_4 → SELF_FUNDED_NO_CSHC
 */
export function mapDbTierToCanonical(dbTier: string): SahMeansTier | null {
  const map: Record<string, SahMeansTier> = {
    tier_1: 'FULL_PENSIONER',
    tier_2: 'PART_PENSIONER',
    tier_3: 'CSHC_HOLDER',
    tier_4: 'SELF_FUNDED_NO_CSHC',
  };
  return map[dbTier] ?? null;
}

/** Reverse mapper: canonical → DB enum value. */
export function mapCanonicalToDbTier(canonical: SahMeansTier): string {
  const map: Record<SahMeansTier, string> = {
    FULL_PENSIONER: 'tier_1',
    PART_PENSIONER: 'tier_2',
    CSHC_HOLDER: 'tier_3',
    SELF_FUNDED_NO_CSHC: 'tier_4',
  };
  return map[canonical];
}

/**
 * Whether a participant's SAH means basis is COMPLETE for readiness/billing — the
 * SAME rule the onboarding-pack DB gate enforces (the
 * `sah_means_tier_contribution_rates` missing-evidence check): a tier must be
 * recorded, AND for the sliding-scale tiers (tier_2 part pensioner / tier_3 CSHC
 * holder) BOTH Services Australia assessed rates must be present. Without them the
 * contribution engine returns null (it refuses to guess), so a client with a
 * sliding tier but no assessed rates is NOT contribution-ready — `hasMeansTier`
 * in `computeSahReadiness` must use this, not a bare `Boolean(tier)`, or the
 * readiness UI goes green while billing is blocked. Takes the DB tier string
 * (`tier_1`..`tier_4`).
 */
export function isSahMeansBasisComplete(
  dbTier: string | null | undefined,
  assessedIndependence: number | null | undefined,
  assessedEverydayLiving: number | null | undefined,
): boolean {
  if (!dbTier) return false;
  const isSliding = dbTier === 'tier_2' || dbTier === 'tier_3';
  if (!isSliding) return true;
  return assessedIndependence != null && assessedEverydayLiving != null;
}

/**
 * Map a stored DB service-category value to the canonical CLINICAL / INDEPENDENCE
 * / EVERYDAY_LIVING form used by the library and regulatory-rates.ts.
 *
 * Accepts BOTH the legacy lowercase enum (clinical / independence /
 * everyday_living) AND the SAH plan-spine uppercase enum (CLINICAL_CARE /
 * INDEPENDENCE / EVERYDAY_LIVING), case-insensitively. Both `clinical` and
 * `clinical_care` map to CLINICAL.
 *
 * Previously this only knew the three lowercase keys, so the persisted spine
 * token `CLINICAL_CARE` mapped to null — causing the service-agreement builder
 * and care-plan composer (which fall through to this helper) to render every
 * clinical line as "Category not recorded" on the binding SAH service agreement
 * and to force the participant-budget summary into "missing contribution basis".
 * (SAH/platform UX audit CAREPLAN-1, 2026-06-20.)
 */
export function mapDbServiceCategoryToCanonical(dbCategory: string): SahServiceCategory | null {
  if (!dbCategory) return null;
  const map: Record<string, SahServiceCategory> = {
    clinical: 'CLINICAL',
    clinical_care: 'CLINICAL',
    independence: 'INDEPENDENCE',
    everyday_living: 'EVERYDAY_LIVING',
  };
  return map[dbCategory.trim().toLowerCase()] ?? null;
}

export function mapCanonicalToDbServiceCategory(canonical: SahServiceCategory): string {
  const map: Record<SahServiceCategory, string> = {
    CLINICAL: 'clinical',
    INDEPENDENCE: 'independence',
    EVERYDAY_LIVING: 'everyday_living',
  };
  return map[canonical];
}

// ─────────────────────────────────────────────────────────────────────────
// AT-HM (Assistive Technology & Home Modifications) tier classification
// ─────────────────────────────────────────────────────────────────────────

export type AtHmTier = 'low' | 'medium' | 'high' | 'exceptional';

/**
 * Returns the AT-HM funding tier for a given estimated cost per Chapter 13.3
 * of the DoH Support at Home Program Manual v4.2 (December 2025), made under
 * Aged Care Rules 2025 Chapter 4.
 *
 * Thresholds pinned to sah_program_rates AT_LOW/AT_MED/AT_HIGH +
 * HM_LOW/HM_MED/HM_HIGH allocation_cap, VERIFIED effective 2026-07-01
 * (P5b defect-1 fix 2026-08-20 — this function and SAH_ATHM_BUDGET_CAPS
 * below were still hardcoded to the superseded 500/2,000/15,000 figures
 * while sah_program_rates already held the current ones):
 *   Low    (tier 1): up to $513 inclusive
 *   Medium (tier 2): $513.01 up to $2,052 inclusive
 *   High   (tier 3): $2,052.01 up to $15,390 inclusive
 *   Exceptional:     above $15,390 — permitted only for Assistive Technology
 *                    with qualifying prescription evidence. Home modifications
 *                    are hard-capped at $15,390 per lifetime.
 *
 * These are a static snapshot (this package has no DB access) — re-pin on
 * the next 1 July indexation alongside the sah_program_rates migration.
 *
 * A null / undefined / non-numeric cost returns null (caller must decide).
 */
export function getAtHmTier(estimatedCost: number | null | undefined): AtHmTier | null {
  if (estimatedCost == null) return null;
  const n = Number(estimatedCost);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n <= 513) return 'low';
  if (n <= 2_052) return 'medium';
  if (n <= 15_390) return 'high';
  return 'exceptional';
}

/**
 * Returns true when the AT-HM request requires a prior Services Australia
 * approval before delivery. Per the DoH program manual 13.2, prior approval
 * is required for the High tier (> $2,000) onward. Low and Medium tiers are
 * provider-approved against the support plan.
 */
export function atHmRequiresPriorApproval(tier: AtHmTier | null): boolean {
  return tier === 'high' || tier === 'exceptional';
}

/**
 * Returns true when the AT-HM request requires a prescription from a
 * qualified health professional. Per manual 13.4.1: ALL home modifications
 * require prescription; some assistive technology benefits from prescription
 * but it is not mandatory for low-risk AT items. We enforce the mandatory
 * home-modification rule here; the AT-HM list governs which specific AT
 * items additionally require prescription (out of scope for this helper).
 */
export function atHmRequiresPrescription(
  requestType: 'assistive_tech' | 'home_modification' | string,
  tier: AtHmTier | null,
): boolean {
  if (requestType === 'home_modification') return true;
  // Assistive technology: Exceptional tier (>$15k) requires OT/health-professional
  // evidence per manual 13.3.1. Below Exceptional, AT prescription is
  // AT-HM-list-driven and enforced via sah_at_hm_list mapping — not here.
  if (requestType === 'assistive_tech' && tier === 'exceptional') return true;
  return false;
}

/**
 * Returns true when a home-modification request above the $15,390 lifetime
 * cap should be rejected per DoH manual 13.3.1: "Funding for high-tier home
 * modifications will be capped [...] per lifetime (this does not
 * include any additional supplement a participant may be eligible for).
 * Lifetime caps of the home modifications high tier will be monitored by
 * Services Australia." Threshold re-pinned to the 2026-07-01 indexed
 * HM_HIGH allocation_cap (sah_program_rates) — was hardcoded to the
 * superseded $15,000 until the P5b defect-1 fix (2026-08-20).
 *
 * "any additional supplement" is the rural/remote (MM6/MM7) uplift — not
 * modelled by this pure function; the database enforcement
 * (transition_sah_at_hm_request_controlled) adds a recorded
 * sah_budget_supplements REMOTE row on top of this base cap.
 *
 * Assistive technology above $15,390 is permitted with evidence.
 */
export function atHmExceedsHomeModificationLifetimeCap(
  requestType: 'assistive_tech' | 'home_modification' | string,
  priorLifetimeHomeModificationSpend: number,
  estimatedCost: number | null | undefined,
): boolean {
  if (requestType !== 'home_modification') return false;
  if (estimatedCost == null) return false;
  return priorLifetimeHomeModificationSpend + Number(estimatedCost) > 15_390;
}
